import { z } from "zod";
import {
	isOrgScopedConfigurationTypesQuery,
	mergeConfigurationTypesById,
	ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP,
	sliceConfigurationTypesPage,
	sortConfigurationTypes,
	stripEnabledParam,
} from "@/utils/configuration-type-list/configuration-type-org-scoped-list.utils";
import { isRequestAborted } from "@/utils/is-request-aborted";
import { shouldShowOrgAdminTicketTypesTableRow } from "@/utils/ticket-type-detail/ticket-type-detail.utils";
import { apiService, organizationIdHeader } from "../api.service";
import { appendEntityDataQueriesToQueryParams } from "../form-definition/entity-data-query-params";
import {
	appendAttachmentRequirementTagId,
	createTypeAttachmentRequirementTag,
	deleteTypeAttachmentRequirementTag,
	editTypeAttachmentRequirementTag,
	getTypeAttachmentRequirement,
	putTypeAttachmentRequirement,
} from "../shared/attachment-requirement.api";
import {
	type GeometrySourceDisplay,
	GeometrySourcesArraySchema,
	type GpsMethodDisplay,
	GpsMethodsArraySchema,
} from "../shared/geometry-catalog.types";
import {
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type NoteDisplay,
	NoteDisplayArraySchema,
	type NoteParams,
} from "../shared/note.types";
import {
	type AuditLogDisplay,
	AuditLogDisplaySchema,
	type CreateTicketInfo,
	CreateTicketInfoSchema,
	type CreateTicketTypeInfo,
	CreateTicketTypeInfoSchema,
	type EditTicketFileInfo,
	type EditTicketInfo,
	EditTicketInfoSchema,
	type EditTicketTypeInfo,
	EditTicketTypeInfoSchema,
	type FeatureCollection,
	FeatureCollectionSchema,
	type FileDisplay,
	FileDisplayArraySchema,
	type GetTicketPrioritiesResponse,
	type GetTicketStatusesResponse,
	type GetTicketTypesResponse,
	normalizeTicketTypesQueryOptions,
	type ServiceResult,
	ServiceResultSchema,
	type SubContractorParams,
	TicketApiError,
	type TicketAssetAssociationParams,
	type TicketAssetInfo,
	TicketAssetInfoArraySchema,
	type TicketAssignmentDisplay,
	TicketAssignmentDisplaySchema,
	type TicketAssignmentParams,
	type TicketDisplay,
	TicketDisplaySchema,
	type TicketDisplayWithUsers,
	TicketDisplayWithUsersSchema,
	type TicketFileParams,
	type TicketFromTextCreate,
	TicketFromTextCreateSchema,
	type TicketFromTextPreview,
	TicketFromTextPreviewSchema,
	type TicketFromTextRequest,
	TicketFromTextRequestSchema,
	type TicketParams,
	TicketPrioritiesArraySchema,
	type TicketRecordAssociationParams,
	type TicketRecordInfo,
	TicketRecordInfoArraySchema,
	TicketStatusesArraySchema,
	type TicketSubContractorDisplay,
	TicketSubContractorDisplaySchema,
	type TicketTypeDisplay,
	TicketTypeDisplaySchema,
	TicketTypesArraySchema,
	type TicketTypesListParams,
	type TicketTypesQueryOptionsInput,
	type UpdateTicketStatusInfo,
	UpdateTicketStatusInfoSchema,
} from "./ticket.types";

function toOrderByQueryParams(
	params?:
		| TicketFileParams
		| NoteParams
		| TicketAssetAssociationParams
		| TicketRecordAssociationParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const { OrderBy, ...rest } = params;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	if (OrderBy?.Fields !== undefined) flat["OrderBy.Fields"] = OrderBy.Fields;
	if (OrderBy?.Descending !== undefined)
		flat["OrderBy.Descending"] = OrderBy.Descending;
	return Object.keys(flat).length ? flat : undefined;
}

// Array schemas for list responses
const TicketsArray = z.array(TicketDisplaySchema);
const TicketAssignmentsArray = z.array(TicketAssignmentDisplaySchema);
const TicketSubContractorsArray = z.array(TicketSubContractorDisplaySchema);
const AuditLogArray = z.array(AuditLogDisplaySchema);

const TICKET_TYPE_ORDER_BY_ACCESSOR: Record<
	string,
	(row: TicketTypeDisplay) => string | number | null | undefined
> = {
	Name: (row) => row.name ?? "",
	Description: (row) => row.description ?? "",
	Id: (row) => row.id ?? "",
};

function filterOrgAdminTicketTypesTableRows(
	rows: TicketTypeDisplay[],
): TicketTypeDisplay[] {
	return rows.filter(shouldShowOrgAdminTicketTypesTableRow);
}

function sortTicketTypes(
	rows: TicketTypeDisplay[],
	input?: TicketTypesListParams,
): TicketTypeDisplay[] {
	return sortConfigurationTypes(
		rows,
		input,
		TICKET_TYPE_ORDER_BY_ACCESSOR,
		"Name",
	);
}

/**
 * Flatten TicketParams so nested BoundingBox is serialized as dot notation
 * (e.g. BoundingBox.MinLatitude=49.48) instead of bracket notation
 * (BoundingBox[MinLatitude]=49.48), which the API expects.
 */
function toTicketQueryParams(
	params?: TicketParams | (TicketParams & { columns?: string }),
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	const { BoundingBox, EntityDataQueries, ...rest } = params;
	if (BoundingBox) {
		if (BoundingBox.MinLatitude !== undefined)
			flat["BoundingBox.MinLatitude"] = BoundingBox.MinLatitude;
		if (BoundingBox.MaxLatitude !== undefined)
			flat["BoundingBox.MaxLatitude"] = BoundingBox.MaxLatitude;
		if (BoundingBox.MinLongitude !== undefined)
			flat["BoundingBox.MinLongitude"] = BoundingBox.MinLongitude;
		if (BoundingBox.MaxLongitude !== undefined)
			flat["BoundingBox.MaxLongitude"] = BoundingBox.MaxLongitude;
	}
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	appendEntityDataQueriesToQueryParams(flat, EntityDataQueries);
	return Object.keys(flat).length ? flat : undefined;
}

/** Form copy on a real ticket type can outlast the shared API timeout. */
const DUPLICATE_TICKET_TYPE_TIMEOUT_MS = 60_000;

export class TicketService {
	// ==================== CORE TICKET OPERATIONS ====================

	/**
	 * Get tickets with optional filtering and pagination
	 * GET /tickets/{start}/{count}
	 */
	async getTickets(
		start: number = 1,
		count: number = 10,
		params?: TicketParams,
	): Promise<TicketDisplay[]> {
		try {
			const response = await apiService.get<TicketDisplay[]>(
				`/tickets/${start}/${count}`,
				{
					params: toTicketQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return TicketsArray.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get tickets: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get tickets count with optional filtering
	 * GET /tickets/count
	 */
	async getTicketsCount(
		params?: TicketParams,
		options?: { signal?: AbortSignal },
	): Promise<number> {
		try {
			const response = await apiService.get<number>("/tickets/count", {
				params: toTicketQueryParams(params),
				headers: organizationIdHeader(params?.OrganizationId),
				signal: options?.signal,
			});

			return z.number().parse(response);
		} catch (error) {
			if (isRequestAborted(error)) throw error;
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get tickets count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single ticket by ID
	 * GET /tickets/{id}
	 */
	async getTicket(
		id: string,
		options?: { organizationId?: string },
	): Promise<TicketDisplayWithUsers> {
		try {
			const response = await apiService.get<TicketDisplayWithUsers>(
				`/tickets/${id}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);

			return TicketDisplayWithUsersSchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new ticket
	 * POST /tickets
	 */
	async createTicket(data: CreateTicketInfo): Promise<ServiceResult> {
		try {
			const validatedData = CreateTicketInfoSchema.parse(data);

			const response = await apiService.post<ServiceResult>(
				"/tickets",
				{
					...(validatedData.name ? { name: validatedData.name } : {}),
					...(validatedData.description
						? { description: validatedData.description }
						: {}),
					...(validatedData.typeId ? { typeId: validatedData.typeId } : {}),
					...(validatedData.priorityId
						? { priorityId: validatedData.priorityId }
						: {}),
					...(validatedData.dueDate ? { dueDate: validatedData.dueDate } : {}),
					...(validatedData.organizationUserIds?.length
						? { organizationUserIds: validatedData.organizationUserIds }
						: {}),
					subOrganizationId: validatedData.subOrganizationId ?? undefined,
					...(validatedData.subcontractorIds?.length
						? { subcontractorIds: validatedData.subcontractorIds }
						: {}),
					...(validatedData.latitude !== undefined
						? { latitude: validatedData.latitude }
						: {}),
					...(validatedData.longitude !== undefined
						? { longitude: validatedData.longitude }
						: {}),
					...(validatedData.geometrySource !== undefined
						? { geometrySource: validatedData.geometrySource }
						: {}),
					...(validatedData.gpsMethod !== undefined
						? { gpsMethod: validatedData.gpsMethod }
						: {}),
					...(validatedData.formData != null
						? { formData: validatedData.formData }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new TicketApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new TicketApiError(
				`Failed to create ticket: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Shared Axios timeout is 15s. One preview or create runs several sequential
	 * LLM calls, and the API allows 110s for each, so these two posts wait longer.
	 */
	private static readonly ticketFromTextTimeoutMs = 10 * 60 * 1000;

	/**
	 * Preview a ticket drafted from free text (LLM).
	 * POST /tickets/from-text/preview
	 */
	async previewTicketFromText(
		data: TicketFromTextRequest,
	): Promise<TicketFromTextPreview> {
		try {
			const { organizationId, ...body } =
				TicketFromTextRequestSchema.parse(data);
			const response = await apiService.post<unknown>(
				"/tickets/from-text/preview",
				{
					text: body.text,
					...(body.subOrganizationId
						? { subOrganizationId: body.subOrganizationId }
						: {}),
					...(body.additionalPrompt
						? { additionalPrompt: body.additionalPrompt }
						: {}),
				},
				{
					headers: organizationIdHeader(organizationId ?? undefined),
					timeout: TicketService.ticketFromTextTimeoutMs,
				},
			);

			const failed = ServiceResultSchema.safeParse(response);
			if (
				failed.success &&
				response &&
				typeof response === "object" &&
				("success" in response || "isSuccess" in response) &&
				!failed.data.success
			) {
				throw TicketApiError.fromServiceResult(failed.data);
			}

			return TicketFromTextPreviewSchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to preview ticket from text: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a ticket drafted from free text (LLM).
	 * POST /tickets/from-text
	 */
	async createTicketFromText(
		data: TicketFromTextRequest,
	): Promise<TicketFromTextCreate> {
		try {
			const { organizationId, ...body } =
				TicketFromTextRequestSchema.parse(data);
			const response = await apiService.post<unknown>(
				"/tickets/from-text",
				{
					text: body.text,
					...(body.subOrganizationId
						? { subOrganizationId: body.subOrganizationId }
						: {}),
					...(body.additionalPrompt
						? { additionalPrompt: body.additionalPrompt }
						: {}),
				},
				{
					headers: organizationIdHeader(organizationId ?? undefined),
					timeout: TicketService.ticketFromTextTimeoutMs,
				},
			);

			const failed = ServiceResultSchema.safeParse(response);
			if (
				failed.success &&
				response &&
				typeof response === "object" &&
				("success" in response || "isSuccess" in response) &&
				!failed.data.success
			) {
				throw TicketApiError.fromServiceResult(failed.data);
			}

			return TicketFromTextCreateSchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to create ticket from text: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a ticket by ID
	 * DELETE /tickets/{ticketId}
	 */
	async deleteTicket(
		id: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/tickets/${id}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to delete ticket ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a ticket by ID
	 * PUT /tickets/{ticketId}
	 */
	async updateTicket(id: string, data: EditTicketInfo): Promise<ServiceResult> {
		try {
			const validatedData = EditTicketInfoSchema.parse(data);

			const response = await apiService.put<ServiceResult>(
				`/tickets/${id}`,
				{
					...(validatedData.name !== undefined
						? { name: validatedData.name }
						: {}),
					...(validatedData.description !== undefined
						? { description: validatedData.description }
						: {}),
					...(validatedData.statusId !== undefined
						? { statusId: validatedData.statusId }
						: {}),
					...(validatedData.typeId !== undefined
						? { typeId: validatedData.typeId }
						: {}),
					...(validatedData.priorityId !== undefined
						? { priorityId: validatedData.priorityId }
						: {}),
					...(validatedData.latitude !== undefined
						? { latitude: validatedData.latitude }
						: {}),
					...(validatedData.longitude !== undefined
						? { longitude: validatedData.longitude }
						: {}),
					...(validatedData.geometrySource !== undefined
						? { geometrySource: validatedData.geometrySource }
						: {}),
					...(validatedData.gpsMethod !== undefined
						? { gpsMethod: validatedData.gpsMethod }
						: {}),
					...(validatedData.subOrganizationId !== undefined
						? { subOrganizationId: validatedData.subOrganizationId }
						: {}),
					...(validatedData.organizationUserIds !== undefined
						? { organizationUserIds: validatedData.organizationUserIds }
						: {}),
					...(validatedData.subContractorIds !== undefined
						? { subContractorIds: validatedData.subContractorIds }
						: {}),
					...(validatedData.dueDate !== undefined
						? { dueDate: validatedData.dueDate }
						: {}),
					...(validatedData.formData !== undefined
						? { formData: validatedData.formData }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new TicketApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new TicketApiError(
				`Failed to update ticket ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a ticket's status
	 * PUT /tickets/{ticketId}/status
	 */
	async updateTicketStatus(
		id: string,
		data: UpdateTicketStatusInfo,
	): Promise<ServiceResult> {
		try {
			const validatedData = UpdateTicketStatusInfoSchema.parse(data);

			const response = await apiService.put<ServiceResult>(
				`/tickets/${id}/status`,
				{
					...(validatedData.organizationId !== undefined
						? { organizationId: validatedData.organizationId }
						: {}),
					...(validatedData.statusId !== undefined
						? { statusId: validatedData.statusId }
						: {}),
				},
				{
					headers: organizationIdHeader(
						validatedData.organizationId ?? undefined,
					),
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new TicketApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new TicketApiError(
				`Failed to update ticket status ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get valid statuses for a ticket
	 * GET /tickets/{id}/valid-statuses
	 */
	async getTicketValidStatuses(
		id: string,
		options?: { organizationId?: string },
	): Promise<GetTicketStatusesResponse> {
		try {
			const response = await apiService.get<GetTicketStatusesResponse>(
				`/tickets/${id}/valid-statuses`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);

			return TicketStatusesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get valid statuses for ticket ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get ticket types
	 * GET /tickets/types
	 */
	async getTicketTypes(options?: {
		organizationId?: string;
		subOrganizationId?: string;
	}): Promise<GetTicketTypesResponse> {
		try {
			const response = await apiService.get<GetTicketTypesResponse>(
				"/tickets/types",
				{
					headers: organizationIdHeader(options?.organizationId),
					...(options?.subOrganizationId
						? { params: { subOrganizationId: options.subOrganizationId } }
						: {}),
				},
			);

			return TicketTypesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a ticket type
	 * POST /tickets/types
	 */
	async createTicketType(
		data: CreateTicketTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validatedData = CreateTicketTypeInfoSchema.parse(data);

			const organizationId = options?.organizationId;
			const response = await apiService.post<ServiceResult>(
				"/tickets/types",
				validatedData,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new TicketApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new TicketApiError(
				`Failed to create ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Duplicate a ticket type. The API copies core config and appends " copy" to the name.
	 * POST /tickets/types/{ticketTypeId}/duplicate
	 */
	async duplicateTicketType(
		ticketTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.post<ServiceResult>(
				`/tickets/types/${ticketTypeId}/duplicate`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
					timeout: DUPLICATE_TICKET_TYPE_TIMEOUT_MS,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to duplicate ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /tickets/types/count
	 */
	async getTicketTypesCount(
		input?: TicketTypesQueryOptionsInput,
	): Promise<number> {
		try {
			const params = normalizeTicketTypesQueryOptions(input);
			if (
				isOrgScopedConfigurationTypesQuery(params) &&
				params.Enabled === undefined
			) {
				const merged = await this.getOrgScopedTicketTypesMerged(params);
				return merged.length;
			}

			return this.fetchTicketTypesCountSingle(params);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket types count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	private async fetchTicketTypesCountSingle(
		params: TicketTypesListParams,
	): Promise<number> {
		const response = await apiService.get<number>("/tickets/types/count", {
			params,
		});
		return z.number().parse(response);
	}

	/**
	 * GET /tickets/types/{start}/{count}/export — `text/csv`
	 */
	async exportTicketTypesCsv(
		start: number,
		count: number,
		input?: TicketTypesQueryOptionsInput,
		columns?: string | null,
	): Promise<Blob> {
		try {
			const params: TicketTypesListParams & { columns?: string } = {
				...normalizeTicketTypesQueryOptions(input),
				...(columns != null && columns !== "" ? { columns } : {}),
			};
			return await apiService.get<Blob>(
				`/tickets/types/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to export ticket types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Ticket types list — GET /tickets/types/{start}/{count}.
	 * Without `page`, fetches all types in batches of 100 to stay within API limits.
	 */
	async getTicketTypesPaged(
		input?: TicketTypesQueryOptionsInput,
		page?: { start: number; count: number },
	): Promise<TicketTypeDisplay[]> {
		try {
			const params = normalizeTicketTypesQueryOptions(input);
			if (page === undefined) {
				const total = await this.getTicketTypesCount(input);
				if (total <= 0) return [];
				return this.fetchAllTicketTypesPaged(total, params);
			}

			if (
				isOrgScopedConfigurationTypesQuery(params) &&
				params.Enabled === undefined
			) {
				const merged = await this.getOrgScopedTicketTypesMerged(params);
				const sorted = sortTicketTypes(merged, params);
				return sliceConfigurationTypesPage(sorted, page.start, page.count);
			}

			return this.fetchTicketTypesPageSingle(page.start, page.count, params);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/** Fetches all ticket types in batches of 100 to respect the API's per-request limit. */
	private async fetchAllTicketTypesPaged(
		total: number,
		params: TicketTypesListParams,
	): Promise<TicketTypeDisplay[]> {
		const PAGE_SIZE = 100;
		const results: TicketTypeDisplay[] = [];
		let fetched = 0;
		while (fetched < total) {
			const batch = await this.fetchTicketTypesPageSingle(
				fetched + 1,
				Math.min(PAGE_SIZE, total - fetched),
				params,
			);
			results.push(...batch);
			fetched += batch.length;
			if (batch.length === 0) break;
		}
		return results;
	}

	/** Enabled + disabled org-scoped rows merged for configuration tables. */
	private async getOrgScopedTicketTypesMerged(
		input?: TicketTypesListParams,
	): Promise<TicketTypeDisplay[]> {
		const baseParams = stripEnabledParam(input) ?? {};
		const enabledParams = { ...baseParams, Enabled: true };
		const disabledParams = { ...baseParams, Enabled: false };
		const [enabledCount, disabledCount] = await Promise.all([
			this.fetchTicketTypesCountSingle(enabledParams),
			this.fetchTicketTypesCountSingle(disabledParams),
		]);

		const [enabledRows, disabledRows] = await Promise.all([
			enabledCount > 0
				? this.fetchTicketTypesPageSingle(
						1,
						Math.min(enabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						enabledParams,
					)
				: Promise.resolve([]),
			disabledCount > 0
				? this.fetchTicketTypesPageSingle(
						1,
						Math.min(disabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						disabledParams,
					)
				: Promise.resolve([]),
		]);

		return filterOrgAdminTicketTypesTableRows(
			mergeConfigurationTypesById(enabledRows, disabledRows),
		);
	}

	private async fetchTicketTypesPageSingle(
		start: number,
		count: number,
		params: TicketTypesListParams,
	): Promise<TicketTypeDisplay[]> {
		const response = await apiService.get<unknown>(
			`/tickets/types/${start}/${count}`,
			{ params },
		);
		return TicketTypesArraySchema.parse(response);
	}

	/**
	 * Get a single ticket type by id
	 * GET /tickets/types/{ticketTypeId}
	 *
	 * `organizationId` — sent as `Organization-id` header (same as job types) so platform
	 * admins in Org Admin resolve the ticket type in the correct organization scope.
	 */
	async getTicketTypeById(
		ticketTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	): Promise<TicketTypeDisplay> {
		try {
			const subOrganizationId = options?.subOrganizationId;
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/tickets/types/${ticketTypeId}`,
				{
					params:
						subOrganizationId && subOrganizationId.length > 0
							? { subOrganizationId }
							: undefined,
					headers:
						organizationId && organizationId.length > 0
							? { "Organization-id": organizationId }
							: undefined,
				},
			);

			const one = Array.isArray(response) ? response[0] : response;
			return TicketTypeDisplaySchema.parse(one);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a ticket type
	 * DELETE /tickets/types/{ticketTypeId}
	 */
	async deleteTicketType(
		ticketTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<unknown>(
				`/tickets/types/${ticketTypeId}`,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			if (response == null || response === "") {
				return {
					success: true,
					message: null,
					affectedId: null,
				};
			}
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to delete ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a ticket type
	 * PUT /tickets/types/{ticketTypeId}
	 */
	async updateTicketType(
		ticketTypeId: string,
		data: EditTicketTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = EditTicketTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/tickets/types/${ticketTypeId}`,
				validated,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to update ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a ticket type for an organization
	 * PUT /tickets/types/{ticketTypeId}/enable
	 */
	async enableTicketType(
		ticketTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/tickets/types/${ticketTypeId}/enable`,
				undefined,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to enable ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Disable a ticket type for an organization
	 * PUT /tickets/types/{ticketTypeId}/disable
	 */
	async disableTicketType(
		ticketTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/tickets/types/${ticketTypeId}/disable`,
				undefined,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw TicketApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to disable ticket type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a specific sub-organization's access to a ticket type
	 * POST /tickets/types/{ticketTypeId}/sub-organization/{subOrgId}/enable
	 */
	async enableTicketTypeSubOrganization(
		ticketTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/tickets/types/${ticketTypeId}/sub-organization/${subOrgId}/enable`,
			undefined,
			{
				headers:
					organizationId && organizationId.length > 0
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Disable a specific sub-organization's access to a ticket type
	 * POST /tickets/types/{ticketTypeId}/sub-organization/{subOrgId}/disable
	 */
	async disableTicketTypeSubOrganization(
		ticketTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/tickets/types/${ticketTypeId}/sub-organization/${subOrgId}/disable`,
			undefined,
			{
				headers:
					organizationId && organizationId.length > 0
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Get ticket priorities
	 * GET /tickets/priorities
	 */
	async getTicketPriorities(options?: {
		organizationId?: string;
	}): Promise<GetTicketPrioritiesResponse> {
		try {
			const response = await apiService.get<GetTicketPrioritiesResponse>(
				"/tickets/priorities",
				{ headers: organizationIdHeader(options?.organizationId) },
			);

			const parsed = TicketPrioritiesArraySchema.parse(response);

			// Sort priorities: Critical > High > Normal > Low
			const priorityOrder: Record<string, number> = {
				Critical: 1,
				High: 2,
				Normal: 3,
				Low: 4,
			};

			return parsed.sort(
				(a, b) => (priorityOrder[a.name] ?? 99) - (priorityOrder[b.name] ?? 99),
			);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket priorities: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get geometry source options for tickets.
	 * GET /tickets/geometry-sources
	 */
	async getGeometrySources(): Promise<GeometrySourceDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				"/tickets/geometry-sources",
			);
			return GeometrySourcesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket geometry sources: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get GPS method options for tickets.
	 * GET /tickets/gps-methods
	 */
	async getGpsMethods(): Promise<GpsMethodDisplay[]> {
		try {
			const response = await apiService.get<unknown>("/tickets/gps-methods");
			return GpsMethodsArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket GPS methods: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get tickets as GeoJSON features
	 * GET /tickets/features/{start}/{count}
	 */
	async getTicketsFeatures(
		start: number = 1,
		count: number = 10,
		params?: TicketParams,
	): Promise<FeatureCollection> {
		try {
			const response = await apiService.get<FeatureCollection>(
				`/tickets/features/${start}/${count}`,
				{
					params: toTicketQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);

			return FeatureCollectionSchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get tickets features: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export tickets to CSV
	 * GET /tickets/{start}/{count}/export
	 */
	async exportTickets(
		start: number,
		count: number,
		params?: TicketParams & { columns?: string },
	): Promise<Blob> {
		try {
			const response = await apiService.get<Blob>(
				`/tickets/${start}/${count}/export`,
				{
					params: toTicketQueryParams(params),
					responseType: "blob",
					headers: {
						Accept: "text/csv",
						...organizationIdHeader(params?.OrganizationId),
					} as Record<string, string>,
				},
			);
			return response;
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to export tickets: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET ASSIGNMENTS ====================

	/**
	 * Get ticket assignments count
	 * GET /tickets/{ticketId}/assignments/count
	 */
	async getTicketAssignmentsCount(
		ticketId: string,
		params?: TicketAssignmentParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/assignments/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket assignments count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get ticket assignments with pagination
	 * GET /tickets/{ticketId}/assignments/{start}/{count}
	 */
	async getTicketAssignments(
		ticketId: string,
		start: number = 1,
		count: number = 10,
		params?: TicketAssignmentParams,
		organizationId?: string,
	): Promise<TicketAssignmentDisplay[]> {
		try {
			const response = await apiService.get<TicketAssignmentDisplay[]>(
				`/tickets/${ticketId}/assignments/${start}/${count}`,
				{
					params,
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			return TicketAssignmentsArray.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket assignments: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET SUB-CONTRACTORS ====================

	/**
	 * Get ticket sub-contractors count
	 * GET /tickets/{ticketId}/sub-contractors/count
	 */
	async getTicketSubContractorsCount(
		ticketId: string,
		params?: SubContractorParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/sub-contractors/count`,
				{ params },
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket sub-contractors count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get ticket sub-contractors with pagination
	 * GET /tickets/{ticketId}/sub-contractors/{start}/{count}
	 */
	async getTicketSubContractors(
		ticketId: string,
		start: number = 1,
		count: number = 10,
		params?: SubContractorParams,
		organizationId?: string,
	): Promise<TicketSubContractorDisplay[]> {
		try {
			const response = await apiService.get<TicketSubContractorDisplay[]>(
				`/tickets/${ticketId}/sub-contractors/${start}/${count}`,
				{
					params,
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			return TicketSubContractorsArray.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket sub-contractors: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET TYPE HISTORY ====================

	/**
	 * Get ticket type history count
	 * GET /tickets/types/{id}/history/count
	 */
	async getTicketTypeHistoryCount(
		ticketTypeId: string,
		organizationId?: string,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/types/${ticketTypeId}/history/count`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket type history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get ticket type history with pagination
	 * GET /tickets/types/{id}/history/{start}/{count}
	 */
	async getTicketTypeHistory(
		ticketTypeId: string,
		start: number = 1,
		count: number = 10,
		organizationId?: string,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/tickets/types/${ticketTypeId}/history/${start}/${count}`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket type history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET HISTORY ====================

	/**
	 * Get ticket history count
	 * GET /tickets/{id}/history/count
	 */
	async getTicketHistoryCount(ticketId: string): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/history/count`,
			);

			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get ticket history with pagination
	 * GET /tickets/{id}/history/{start}/{count}
	 */
	async getTicketHistory(
		ticketId: string,
		start: number = 1,
		count: number = 10,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/tickets/${ticketId}/history/${start}/${count}`,
			);

			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) {
				throw error;
			}
			throw new TicketApiError(
				`Failed to get ticket history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET ASSET ASSOCIATIONS ====================

	/**
	 * List assets linked to a ticket (junction rows).
	 * GET /tickets/{ticketId}/assets/{start}/{count}
	 */
	async getTicketAssets(
		ticketId: string,
		start: number = 1,
		count: number = 50,
		params?: TicketAssetAssociationParams,
		options?: { organizationId?: string },
	): Promise<TicketAssetInfo[]> {
		try {
			const response = await apiService.get<unknown>(
				`/tickets/${ticketId}/assets/${start}/${count}`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return TicketAssetInfoArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get assets for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count assets linked to a ticket.
	 * GET /tickets/{ticketId}/assets/count
	 */
	async getTicketAssetsCount(
		ticketId: string,
		params?: TicketAssetAssociationParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/assets/count`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get asset count for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Link an existing asset to a ticket.
	 * POST /tickets/{ticketId}/assets/{assetId}
	 */
	async linkTicketAsset(
		ticketId: string,
		assetId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/tickets/${ticketId}/assets/${assetId}`,
				undefined,
				{ headers: organizationIdHeader(options?.organizationId) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) throw TicketApiError.fromServiceResult(result);
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to link asset ${assetId} to ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unlink an asset from a ticket.
	 * DELETE /tickets/{ticketId}/assets/{assetId}
	 */
	async unlinkTicketAsset(
		ticketId: string,
		assetId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/tickets/${ticketId}/assets/${assetId}`,
				{ headers: organizationIdHeader(options?.organizationId) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) throw TicketApiError.fromServiceResult(result);
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to unlink asset ${assetId} from ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET RECORD ASSOCIATIONS ====================

	/**
	 * List records linked to a ticket (junction rows).
	 * GET /tickets/{ticketId}/records/{start}/{count}
	 */
	async getTicketRecords(
		ticketId: string,
		start: number = 1,
		count: number = 50,
		params?: TicketRecordAssociationParams,
		options?: { organizationId?: string },
	): Promise<TicketRecordInfo[]> {
		try {
			const response = await apiService.get<unknown>(
				`/tickets/${ticketId}/records/${start}/${count}`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return TicketRecordInfoArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get records for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count records linked to a ticket.
	 * GET /tickets/{ticketId}/records/count
	 */
	async getTicketRecordsCount(
		ticketId: string,
		params?: TicketRecordAssociationParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/records/count`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get record count for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Link an existing record to a ticket.
	 * POST /tickets/{ticketId}/records/{recordId}
	 */
	async linkTicketRecord(
		ticketId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/tickets/${ticketId}/records/${recordId}`,
				undefined,
				{ headers: organizationIdHeader(options?.organizationId) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) throw TicketApiError.fromServiceResult(result);
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to link record ${recordId} to ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unlink a record from a ticket.
	 * DELETE /tickets/{ticketId}/records/{recordId}
	 */
	async unlinkTicketRecord(
		ticketId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/tickets/${ticketId}/records/${recordId}`,
				{ headers: organizationIdHeader(options?.organizationId) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) throw TicketApiError.fromServiceResult(result);
			return result;
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to unlink record ${recordId} from ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET FILES ====================

	/**
	 * List ticket attachments with pagination.
	 * GET /tickets/{ticketId}/files/{start}/{count}
	 */
	async getTicketFiles(
		ticketId: string,
		start: number = 1,
		count: number = 50,
		params?: TicketFileParams,
		options?: { organizationId?: string },
	): Promise<FileDisplay[]> {
		try {
			const response = await apiService.get<FileDisplay[]>(
				`/tickets/${ticketId}/files/${start}/${count}`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return FileDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get files for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count ticket attachments.
	 * GET /tickets/{ticketId}/files/count
	 */
	async getTicketFilesCount(
		ticketId: string,
		params?: TicketFileParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/files/count`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get file count for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Upload a ticket attachment.
	 * POST /tickets/{ticketId}/files — multipart field name `File`.
	 */
	async uploadTicketFile(
		ticketId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("File", file, filename);
			appendAttachmentRequirementTagId(
				formData,
				options?.entityAttachmentRequirementTagId,
			);

			await apiService.post(`/tickets/${ticketId}/files`, formData, {
				params: options?.description
					? { Description: options.description }
					: undefined,
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to upload file for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a ticket attachment.
	 * DELETE /tickets/{ticketId}/files/{fileId}
	 */
	async deleteTicketFile(
		ticketId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/tickets/${ticketId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to delete file ${fileId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Rename / update a ticket attachment metadata.
	 * PUT /tickets/{ticketId}/files/{ticketFileId}
	 */
	async editTicketFile(
		ticketId: string,
		ticketFileId: string,
		info: EditTicketFileInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.put(`/tickets/${ticketId}/files/${ticketFileId}`, info, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to edit file ${ticketFileId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch a ticket file as a Blob.
	 * GET /tickets/{ticketId}/files/{fileId}
	 */
	async getTicketFile(
		ticketId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/tickets/${ticketId}/files/${fileId}`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get file ${fileId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Fetch a ticket file thumbnail as a Blob.
	 * GET /tickets/{ticketId}/files/{fileId}/thumbnail
	 */
	async getTicketFileThumbnail(
		ticketId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/tickets/${ticketId}/files/${fileId}/thumbnail`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get thumbnail for file ${fileId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Probe whether a ticket file exists (headers only).
	 * HEAD /tickets/{ticketId}/files/{fileId}
	 */
	async headTicketFile(
		ticketId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.head(`/tickets/${ticketId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to head file ${fileId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Replace ticket file content.
	 * PATCH /tickets/{ticketId}/files/{fileId} — multipart field name `file`.
	 */
	async replaceTicketFile(
		ticketId: string,
		fileId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("file", file, filename);

			await apiService.patch(`/tickets/${ticketId}/files/${fileId}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to replace file ${fileId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== TICKET NOTES ====================

	/**
	 * List ticket notes with pagination.
	 * GET /tickets/{ticketId}/notes/{start}/{count}
	 */
	async getTicketNotes(
		ticketId: string,
		start: number = 1,
		count: number = 50,
		params?: NoteParams,
		options?: { organizationId?: string },
	): Promise<NoteDisplay[]> {
		try {
			const response = await apiService.get<NoteDisplay[]>(
				`/tickets/${ticketId}/notes/${start}/${count}`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return NoteDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get notes for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count ticket notes.
	 * GET /tickets/{ticketId}/notes/count
	 */
	async getTicketNotesCount(
		ticketId: string,
		params?: NoteParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/tickets/${ticketId}/notes/count`,
				{
					params: toOrderByQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get note count for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a ticket note.
	 * POST /tickets/{ticketId}/notes
	 */
	async createTicketNote(
		ticketId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.post(`/tickets/${ticketId}/notes`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to create note for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a ticket note.
	 * PUT /tickets/{ticketId}/notes/{noteId}
	 */
	async updateTicketNote(
		ticketId: string,
		noteId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.put(`/tickets/${ticketId}/notes/${noteId}`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to update note ${noteId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a ticket note.
	 * DELETE /tickets/{ticketId}/notes/{noteId}
	 */
	async deleteTicketNote(
		ticketId: string,
		noteId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/tickets/${ticketId}/notes/${noteId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to delete note ${noteId} for ticket ${ticketId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async getTicketTypeAttachmentRequirement(
		ticketTypeId: string,
		options?: { organizationId?: string },
	) {
		try {
			return await getTypeAttachmentRequirement(
				"tickets",
				ticketTypeId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to get attachment requirement for ticket type ${ticketTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async putTicketTypeAttachmentRequirement(
		ticketTypeId: string,
		minimumTotalAttachments: number,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await putTypeAttachmentRequirement(
				"tickets",
				ticketTypeId,
				minimumTotalAttachments,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to save attachment requirement for ticket type ${ticketTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async createTicketTypeAttachmentRequirementTag(
		ticketTypeId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder?: number | null;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await createTypeAttachmentRequirementTag(
				"tickets",
				ticketTypeId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to create attachment requirement tag for ticket type ${ticketTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async editTicketTypeAttachmentRequirementTag(
		ticketTypeId: string,
		tagId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder: number;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await editTypeAttachmentRequirementTag(
				"tickets",
				ticketTypeId,
				tagId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to edit attachment requirement tag ${tagId} for ticket type ${ticketTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async deleteTicketTypeAttachmentRequirementTag(
		ticketTypeId: string,
		tagId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await deleteTypeAttachmentRequirementTag(
				"tickets",
				ticketTypeId,
				tagId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof TicketApiError) throw error;
			throw new TicketApiError(
				`Failed to delete attachment requirement tag ${tagId} for ticket type ${ticketTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

// Create and export a singleton instance
export const ticketService = new TicketService();
